import { useCallback, useRef, useEffect } from 'react';
import { useChatWebSocketService } from '../context/ChatWebSocketServiceContext.jsx';
import { WebSocketScope } from '../constants/websocketScope.js';
import { DeltaBuffer } from '../utils/deltaBuffer.js';
import { MessageEvent, MessageStatus } from '../constants/streamingConstants.js';
import { detectContentFormat } from '../utils/contentUtils.js';
import {
  NOTICE_GRACE_MS,
  buildTerminalNotice,
  clearTextSeen,
  hasTextSeen,
  markTextSeen,
  removeNoReplyNotice,
  upsertNoticeRow,
} from '../utils/terminalNotice.js';

const CONVERSATION_SCOPE = `scope:${WebSocketScope.CONVERSATION}`;

const EVENT_LOWER = Object.fromEntries(
  Object.entries(MessageEvent).map(([k, v]) => [k, v.toLowerCase()])
);

/**
 * Human-readable fallback labels for the run lifecycle statuses the workflow
 * pushes (QUEUED → GENERATING → …). The UI translates these via
 * `chatPanel.status.*`, falling back to the label here when a key is missing.
 */
const LIFECYCLE_STATUS_LABEL = {
  QUEUED: 'Queued…',
  IN_PROGRESS: 'Working…',
  WRITING: 'Writing…',
  GENERATING: 'Generating response…',
  WORKING: 'Working…',
};

/** Fallback: clear typing if no new delta for 2s (backend may not send completion events) */
const NO_DELTA_TYPING_STOP_MS = 2000;

/**
 * Safety net for delta-less workflow runs: the per-delta stall timers
 * (scheduleTypingStopFallback + DeltaBuffer) are only armed from processDelta,
 * so a run that never streams deltas can never trigger them. If the terminal
 * `chat.response.completed` is lost (e.g. the socket flapped during a long
 * idle gap), the "Working…" indicator would otherwise hang forever. This
 * watchdog is reset on every incoming event for the ACTIVE conversation and
 * clears the indicators once the stream has been fully silent for the window.
 * It is a fallback only — the real teardown comes from handleCompleted.
 */
const LIFECYCLE_INACTIVITY_STOP_MS = 180_000;

/** Same conversation (compare as strings to avoid type mismatch) */
function isSameConversation(currentId, payloadId) {
  if (!currentId) return true;
  if (!payloadId) return false;
  return String(currentId) === String(payloadId);
}

/**
 * Normalize payload: handle both payload.context + payload.data (new) and flat payload (legacy).
 * For legacy format (flat payload without payload.data), do NOT use data.content fallback - it echoes user's question.
 */
function normalizePayload(message, nextStreamId) {
  const raw = message?.payload ?? message?.data ?? message ?? {};
  const context = raw.context ?? {};
  const hasExplicitData = !!raw.data && raw.data !== raw;
  const data = hasExplicitData ? raw.data : raw;

  // Workflow-pushed events (progress/milestone messages, HITL cards) don't
  // always carry the conversation id in the same field the langgraph SSE stream
  // uses. Check context, the envelope, AND the inner data blob so these events
  // are matched to the active conversation instead of being dropped.
  const conversationUuid =
    context.conversation_id ??
    context.conversationId ??
    context.conversation_uuid ??
    raw.conversationUuid ??
    raw.conversation_uuid ??
    raw.conversationId ??
    raw.conversation_id ??
    data.conversation_uuid ??
    data.conversationUuid ??
    data.conversation_id;

  // Use || so empty string falls through (backend often sends assistant_message_id: "" or null)
  const messageId =
    context.assistant_message_id ||
    context.response_id ||
    message?.ref_id ||
    data.messageId ||
    data.message_id ||
    data.message_uuid ||
    raw.messageId ||
    raw.message_id ||
    'stream-' + nextStreamId();

  const chunkContent =
    data.text ??
    data.chunkContent ??
    data.chunk_content ??
    (hasExplicitData ? data.content : undefined);

  // Full text for a committed / end-message push (a complete message delivered
  // as a single event rather than a delta stream — e.g. workflow milestones).
  // Core's canonical committed shape nests the text under `data.message`
  // (`{ status: "FINAL", text }`), so descend into it as well — otherwise the
  // frame dies silently at the empty-text guard in handleCommittedMessage.
  const fullContent =
    data.content ??
    data.text ??
    data.formatted_text ??
    data.formattedText ??
    data.message_text ??
    data.messageText ??
    data.message?.text ??
    data.message?.content ??
    null;

  // The langgraph `output_item.committed` frame nests the message under
  // `data.item` (`{ id, role, content, type }`). That text was already streamed
  // as deltas, so it must NOT be rendered again (reading it as `fullContent`
  // put a second copy of every reply in the run's activity group). It only
  // proves the run had visible text, which the "no reply" notice needs.
  const committedItemText = data.item?.content ?? data.item?.text ?? null;

  const deltaIndex = data.index ?? data.chunkIndex ?? data.chunk_index;
  const sequenceId = message?.sequence;

  // Transient progress pings ("Reading your document…") are engine status
  // updates, not persisted messages. Backend sends an explicit boolean on
  // committed frames (never an absent key). Treat only an explicit `true` as
  // transient so any legacy/permanent frame defaults to a bubble.
  const transient = data.transient === true;

  // The run's terminal message ("Discovery … is published"). It is the ONLY
  // workflow message that renders as a regular bubble; every other frame of
  // the run (progress pings AND intermediate milestones) belongs inside the
  // run-activity accordion. Explicit `true` only, so legacy frames that
  // predate the flag keep today's bubble behaviour.
  const final = data.final === true;

  // Stable per-run id shared by every frame of a run (transient pings,
  // permanent commits, and the final `completed`). Used to group a run's
  // transient progress steps into a single inline activity accordion.
  const responseId =
    context.response_id ??
    context.responseId ??
    data.response_id ??
    data.responseId ??
    null;

  return {
    context,
    data,
    hasExplicitData,
    conversationUuid,
    messageId,
    chunkContent,
    fullContent,
    committedItemText,
    transient,
    final,
    responseId,
    deltaIndex,
    sequenceId,
  };
}

/**
 * WebSocket subscription for chat streaming.
 * Handles 25+ event types from backend transport envelope.
 * Uses DeltaBuffer for ordered delta flushing.
 */
export const useHomeChatStreaming = (options = {}) => {
  const streamIdCounterRef = useRef(0);
  // Instance-scoped nextStreamId prevents ID collisions across multiple panels
  const nextStreamId = useCallback(() => ++streamIdCounterRef.current, []);
  const normalize = useCallback((msg) => normalizePayload(msg, nextStreamId), [nextStreamId]);
  const websocketService = useChatWebSocketService();
  const {
    conversationId,
    setMessages,
    messagesRef,
    setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  } = options;

  const bufferRef = useRef(null);
  const currentMessageIdRef = useRef(null);
  const usedMessageIdsRef = useRef(new Set());
  const messageIdCounterRef = useRef(0);
  const completedReceivedRef = useRef(false);
  const graceTimerRef = useRef(null);
  const lastDeltaTimerRef = useRef(null);
  const activityWatchdogRef = useRef(null);
  const prevConversationIdRef = useRef(conversationId);
  const activeConversationIdRef = useRef(conversationId);
  // Runs (by response_id) for which visible text was seen — a delta or a
  // non-empty committed frame. Read at the terminal event to decide whether
  // the turn ended with nothing to show (→ inline "no reply" notice).
  const textSeenForRunRef = useRef(new Set());
  // Pending "no reply" decisions (see handleCompleted); dropped when the
  // conversation changes (the run belonged to the old thread) and on unmount.
  const noticeTimersRef = useRef(new Set());
  const clearNoticeTimers = useCallback(() => {
    noticeTimersRef.current.forEach((timer) => clearTimeout(timer));
    noticeTimersRef.current.clear();
  }, []);

  const refs = useRef({
    setMessages,
    setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  });
  useEffect(() => {
    refs.current = {
      setMessages,
      setAgentIsTyping,
      setCurrentActivity,
      setToolCalls,
      setHitlRequest,
      setSources,
      setUiBlocks,
      setMessageStatus,
      setPlanSteps,
      setIsStreaming,
    };
  }, [
    setMessages,
    setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  ]);

  const updateMessageContent = useCallback(
    (messageId, content) => {
      const setter = refs.current.setMessages;
      if (!setter) return;
      setter((prev) => {
        const next = [...prev];
        const idx = next.findIndex((m) => m.id === messageId || m.messageUuid === messageId);
        const base = idx >= 0 ? next[idx] : {};
        const msg = {
          id: messageId,
          uuid: messageId,
          messageUuid: messageId,
          content,
          sourceContent: content,
          contentFormat: detectContentFormat(content),
          role: 'assistant',
          createdAt: base.createdAt ?? new Date().toISOString(),
          timestamp: base.timestamp ?? new Date().toISOString(),
          conversationId: activeConversationIdRef.current ?? base.conversationId ?? null,
          attachments: base.attachments ?? [],
          metadata: base.metadata ?? {},
        };
        if (idx >= 0) {
          next[idx] = { ...base, ...msg };
        } else {
          next.push(msg);
        }
        return next;
      });
    },
    []
  );

  /**
   * Transient progress pings for a run are grouped into a single inline
   * "activity" element (a collapsible accordion), inserted in message-flow
   * order where the first ping arrived and updated in place for later pings.
   * Session-only: transient frames have no DB row, so the group is not rebuilt
   * from REST history on reload (Level 1 behaviour).
   */
  const upsertActivityStep = useCallback((responseId, stepId, text, kind = 'progress') => {
    const setter = refs.current.setMessages;
    if (!setter || !responseId) return;
    const groupId = `activity:${responseId}`;
    setter((prev) => {
      const idx = prev.findIndex((m) => m.id === groupId);
      if (idx >= 0) {
        const group = prev[idx];
        const steps = Array.isArray(group.steps) ? group.steps : [];
        if (stepId && steps.some((s) => s.id === stepId)) return prev;
        const nextSteps = [...steps, { id: stepId ?? `${groupId}:${steps.length}`, text, kind }];
        const next = [...prev];
        next[idx] = { ...group, steps: nextSteps, activityStatus: 'running' };
        return next;
      }
      const group = {
        id: groupId,
        role: 'activity_group',
        responseId,
        steps: [{ id: stepId ?? `${groupId}:0`, text, kind }],
        activityStatus: 'running',
        conversationId: activeConversationIdRef.current ?? null,
        createdAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
      };
      return [...prev, group];
    });
  }, []);

  const finalizeActivityGroup = useCallback((responseId) => {
    const setter = refs.current.setMessages;
    if (!setter || !responseId) return;
    const groupId = `activity:${responseId}`;
    setter((prev) => {
      const idx = prev.findIndex((m) => m.id === groupId);
      if (idx < 0 || prev[idx].activityStatus === 'done') return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], activityStatus: 'done' };
      return next;
    });
  }, []);

  /**
   * Terminal-state notice ("no reply" / "failed") for a run, as a session-only
   * pseudo-row in the thread (same precedent as the activity group). Upserted
   * BEFORE resetStreamingState so the terminal state stays visible instead of
   * being wiped in the same tick as the typing indicators.
   */
  const upsertTerminalNotice = useCallback((notice) => {
    const setter = refs.current.setMessages;
    if (!setter || !notice) return;
    setter((prev) => upsertNoticeRow(prev, notice));
  }, []);

  /** Record visible text for a run; a late arrival lifts its "no reply". */
  const noteTextSeen = useCallback((responseId, messageId = null) => {
    const seen = textSeenForRunRef.current;
    if (hasTextSeen(seen, responseId)) return;
    markTextSeen(seen, responseId);
    const setter = refs.current.setMessages;
    if (!setter) return;
    setter((prev) => removeNoReplyNotice(prev, responseId, messageId));
  }, []);

  const ensureBuffer = useCallback(
    (messageId) => {
      if (currentMessageIdRef.current !== messageId) {
        if (bufferRef.current) {
          bufferRef.current.destroy();
          bufferRef.current = null;
        }
        if (graceTimerRef.current) {
          clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
        completedReceivedRef.current = false;
        currentMessageIdRef.current = messageId;
        refs.current.setMessageStatus?.(null);
      }
      if (!bufferRef.current) {
        bufferRef.current = new DeltaBuffer({
          onFlush: (text) => updateMessageContent(messageId, text),
          inactivityTimeout: 30000,
          graceMs: 3000,
        });
      }
      return bufferRef.current;
    },
    [updateMessageContent]
  );

  const resetStreamingState = useCallback(() => {
    if (lastDeltaTimerRef.current) {
      clearTimeout(lastDeltaTimerRef.current);
      lastDeltaTimerRef.current = null;
    }
    if (activityWatchdogRef.current) {
      clearTimeout(activityWatchdogRef.current);
      activityWatchdogRef.current = null;
    }
    if (bufferRef.current) {
      bufferRef.current.destroy();
      bufferRef.current = null;
    }
    if (currentMessageIdRef.current) {
      usedMessageIdsRef.current.add(currentMessageIdRef.current);
    }
    currentMessageIdRef.current = null;
    completedReceivedRef.current = false;
    refs.current.setAgentIsTyping?.(false);
    refs.current.setIsStreaming?.(false);
    refs.current.setCurrentActivity?.(null);
    refs.current.setToolCalls?.([]);
    refs.current.setMessageStatus?.(null);
    refs.current.setPlanSteps?.([]);
    refs.current.setSources?.([]);
  }, []);

  const scheduleTypingStopFallback = useCallback(() => {
    if (lastDeltaTimerRef.current) clearTimeout(lastDeltaTimerRef.current);
    lastDeltaTimerRef.current = setTimeout(() => {
      lastDeltaTimerRef.current = null;
      if (!completedReceivedRef.current) {
        refs.current.setAgentIsTyping?.(false);
        refs.current.setIsStreaming?.(false);
      }
    }, NO_DELTA_TYPING_STOP_MS);
  }, []);

  const bumpActivityWatchdog = useCallback(() => {
    if (activityWatchdogRef.current) clearTimeout(activityWatchdogRef.current);
    activityWatchdogRef.current = setTimeout(() => {
      activityWatchdogRef.current = null;
      refs.current.setAgentIsTyping?.(false);
      refs.current.setIsStreaming?.(false);
      refs.current.setCurrentActivity?.(null);
    }, LIFECYCLE_INACTIVITY_STOP_MS);
  }, []);

  useEffect(() => {
    activeConversationIdRef.current = conversationId;
    const prev = prevConversationIdRef.current;
    prevConversationIdRef.current = conversationId;
    if (bufferRef.current) {
      bufferRef.current.destroy();
      bufferRef.current = null;
    }
    currentMessageIdRef.current = null;
    usedMessageIdsRef.current.clear();
    textSeenForRunRef.current.clear();
    clearNoticeTimers();
    completedReceivedRef.current = false;
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    if (lastDeltaTimerRef.current) {
      clearTimeout(lastDeltaTimerRef.current);
      lastDeltaTimerRef.current = null;
    }
    if (activityWatchdogRef.current) {
      clearTimeout(activityWatchdogRef.current);
      activityWatchdogRef.current = null;
    }
    if (prev && conversationId && prev !== conversationId) {
      resetStreamingState();
    }
  }, [conversationId, resetStreamingState, clearNoticeTimers]);

  const resolveMessageId = useCallback(
    (rawMessageId, convUuid) => {
      if (currentMessageIdRef.current) return currentMessageIdRef.current;

      const existsInHistory = rawMessageId && (messagesRef?.current || []).some(
        (m) => m.id === rawMessageId || m.messageUuid === rawMessageId
      );

      if (
        rawMessageId &&
        rawMessageId !== convUuid &&
        rawMessageId !== activeConversationIdRef.current &&
        !usedMessageIdsRef.current.has(rawMessageId) &&
        !existsInHistory
      ) {
        return rawMessageId;
      }
      const id = ++messageIdCounterRef.current;
      return `assistant-${id}-${Math.random().toString(36).slice(2, 8)}`;
    },
    [messagesRef]
  );

  const processDelta = useCallback(
    (message) => {
      const { conversationUuid, messageId: rawMsgId, chunkContent, deltaIndex, sequenceId, responseId } =
        normalize(message);

      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      if ((chunkContent ?? '') === '' || !rawMsgId) return;
      noteTextSeen(responseId, rawMsgId);

      const isConvIdAsMessageId =
        rawMsgId === conversationUuid || rawMsgId === activeConversationIdRef.current;
      const hasFlushed =
        currentMessageIdRef.current &&
        bufferRef.current &&
        bufferRef.current.flushedContent.length > 0;
      const shouldStartNew = deltaIndex === 0 && hasFlushed;

      if (shouldStartNew) {
        usedMessageIdsRef.current.add(currentMessageIdRef.current);
        bufferRef.current.destroy();
        bufferRef.current = null;
        currentMessageIdRef.current = null;
      }

      const messageId = (shouldStartNew || isConvIdAsMessageId)
        ? resolveMessageId(null, conversationUuid)
        : resolveMessageId(rawMsgId, conversationUuid);

      refs.current.setAgentIsTyping?.(true);
      refs.current.setIsStreaming?.(true);
      // Real tokens are flowing — the streamed bubble is now the feedback, so
      // drop the lifecycle status label (Queued/Generating…) to avoid overlap.
      refs.current.setCurrentActivity?.(null);

      const buffer = ensureBuffer(messageId);
      const effectiveIndex = deltaIndex ?? sequenceId ?? buffer.getNextAppendIndex();
      buffer.push(
        typeof effectiveIndex === 'number' ? effectiveIndex : buffer.getNextAppendIndex(),
        chunkContent,
        sequenceId
      );
      scheduleTypingStopFallback();
    },
    [ensureBuffer, scheduleTypingStopFallback, resolveMessageId, noteTextSeen]
  );

  const handleOutputTextDelta = processDelta;
  const handleLegacyDelta = processDelta;

  const handleCompleted = useCallback(
    (message) => {
      const { conversationUuid, responseId, messageId } = normalize(message);

      // Drop only on an explicit mismatch: a terminal event that omits the
      // conversation id must never be discarded, or the run stays "running".
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;

      // Freeze the run's activity accordion into its static "Ran N steps"
      // summary (indicator off, still expandable).
      finalizeActivityGroup(responseId);

      // A run that ended without a single visible token (empty synthesis)
      // leaves the thread unchanged — surface it. Delta-less workflow runs
      // whose committed milestone frames carried text count as text seen, so
      // "completed with no buffered delta" alone never triggers this.
      //
      // The decision waits NOTICE_GRACE_MS: `completed` can reach the browser
      // before the first delta of the same run (observed live, response ids
      // empty on both), so "no text yet" at this instant proves nothing.
      const seen = textSeenForRunRef.current;
      const fallbackId = currentMessageIdRef.current ?? messageId;
      const conversationId = activeConversationIdRef.current ?? conversationUuid ?? null;
      if (hasTextSeen(seen, responseId)) {
        clearTextSeen(seen, responseId);
      } else {
        const timers = noticeTimersRef.current;
        const timer = setTimeout(() => {
          timers.delete(timer);
          // The user may have switched conversations during the grace window
          // (the switch also clears these timers; this is the belt to that
          // brace): the thread must still be exactly the one the run ran in.
          // Strict compare on purpose — isSameConversation treats a null
          // active id as a match, which would drop the notice into New chat.
          if (
            String(activeConversationIdRef.current ?? '') !== String(conversationId ?? '')
          ) {
            clearTextSeen(seen, responseId);
            return;
          }
          upsertTerminalNotice(
            buildTerminalNotice({
              event: 'completed',
              textSeen: hasTextSeen(seen, responseId),
              responseId,
              conversationId,
              fallbackId,
            })
          );
          clearTextSeen(seen, responseId);
        }, NOTICE_GRACE_MS);
        timers.add(timer);
      }

      completedReceivedRef.current = true;
      const buffer = bufferRef.current;
      if (buffer) {
        buffer.markFin(null);
        graceTimerRef.current = setTimeout(() => {
          graceTimerRef.current = null;
          resetStreamingState();
        }, 3000);
      } else {
        resetStreamingState();
      }
    },
    [resetStreamingState, finalizeActivityGroup, normalize, upsertTerminalNotice]
  );

  const handleOutputItemDone = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setAgentIsTyping?.(false);
    },
    []
  );

  const handleFinalizing = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setAgentIsTyping?.(false);
    },
    []
  );

  const handleFailed = useCallback(
    (message) => {
      const { conversationUuid, responseId, messageId, data } = normalize(message);
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      // `messageStatus` is nulled by resetStreamingState in the same tick and
      // nothing renders it, so the failure was invisible. Pin it to the thread
      // as a notice row (with the backend's error code) before the reset.
      const seen = textSeenForRunRef.current;
      upsertTerminalNotice(
        buildTerminalNotice({
          event: 'failed',
          textSeen: hasTextSeen(seen, responseId),
          responseId,
          conversationId: activeConversationIdRef.current ?? conversationUuid ?? null,
          data,
          fallbackId: currentMessageIdRef.current ?? messageId,
        })
      );
      clearTextSeen(seen, responseId);
      refs.current.setMessageStatus?.(MessageStatus.FAILED);
      resetStreamingState();
    },
    [resetStreamingState, normalize, upsertTerminalNotice]
  );

  const handleIncomplete = useCallback(
    (message) => {
      const { conversationUuid, responseId } = normalize(message);
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      clearTextSeen(textSeenForRunRef.current, responseId);
      refs.current.setMessageStatus?.(MessageStatus.INCOMPLETE);
      resetStreamingState();
    },
    [resetStreamingState, normalize]
  );

  const handleCancelled = useCallback(
    (message) => {
      const { conversationUuid, responseId } = normalize(message);
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      clearTextSeen(textSeenForRunRef.current, responseId);
      refs.current.setMessageStatus?.(MessageStatus.CANCELLED);
      resetStreamingState();
    },
    [resetStreamingState, normalize]
  );

  /**
   * Run lifecycle status feedback (created / in_progress / writing /
   * output_item.added). These events carry the workflow's *state* (QUEUED,
   * GENERATING, …) rather than text. They had no handler, so the user got no
   * signal about what the workflow was doing during delta-less phases (e.g. a
   * run that does tool work then pauses at a HITL gate). Surface the status via
   * `currentActivity` and keep the streaming indicator active so the feedback
   * bar shows.
   */
  const handleLifecycleStatus = useCallback(
    (message, phase) => {
      const { conversationUuid } = normalize(message);
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      const rawStatus = String(data.status ?? '').toUpperCase();
      const status =
        rawStatus ||
        (phase === 'writing' ? 'WRITING' : phase === 'in_progress' ? 'IN_PROGRESS' : 'WORKING');
      refs.current.setIsStreaming?.(true);
      refs.current.setAgentIsTyping?.(true);
      refs.current.setCurrentActivity?.({
        status,
        kind: 'lifecycle',
        label: LIFECYCLE_STATUS_LABEL[status] ?? LIFECYCLE_STATUS_LABEL.WORKING,
      });
    },
    [normalize]
  );

  const handleActivityStarted = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      refs.current.setCurrentActivity?.({
        label: data.label ?? data.description ?? 'Processing...',
        kind: data.kind ?? 'default',
      });
    },
    []
  );

  const handleActivityDone = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setCurrentActivity?.(null);
    },
    []
  );

  const handleToolCallStarted = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      refs.current.setToolCalls?.((prev) => [
        ...prev,
        {
          id: data.id ?? data.tool_call_id ?? Date.now(),
          name: data.name ?? data.tool_name ?? 'Tool',
          status: 'started',
          startedAt: new Date().toISOString(),
        },
      ]);
    },
    []
  );

  const handleToolCallCompleted = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      const id = data.id ?? data.tool_call_id;
      refs.current.setToolCalls?.((prev) =>
        prev.map((tc) =>
          tc.id === id
            ? { ...tc, status: 'completed', completedAt: new Date().toISOString() }
            : tc
        )
      );
    },
    []
  );

  const handleToolCallFailed = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      const id = data.id ?? data.tool_call_id;
      refs.current.setToolCalls?.((prev) =>
        prev.map((tc) =>
          tc.id === id ? { ...tc, status: 'failed', completedAt: new Date().toISOString() } : tc
        )
      );
    },
    []
  );

  const handlePlanPublished = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const steps = payload.data?.steps ?? payload.steps ?? [];
      refs.current.setPlanSteps?.(steps);
    },
    []
  );

  const handlePlanStepUpdated = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const step = payload.data ?? payload;
      refs.current.setPlanSteps?.((prev) =>
        prev.map((s) => (s.id === step.id ? { ...s, ...step } : s))
      );
    },
    []
  );

  const handleSourcesFinal = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const sourcesList = payload.data?.sources ?? payload.sources ?? [];
      refs.current.setSources?.(sourcesList);
    },
    []
  );

  const handleInputRequired = useCallback(
    (message) => {
      const { conversationUuid } = normalize(message);
      // Only drop on an EXPLICIT conversation mismatch. A workflow-pushed gate
      // that omits the conversation id (or nests it where normalize can't reach)
      // must still surface in the active conversation rather than be swallowed —
      // the durable inbox is a fallback, not the primary live path.
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      refs.current.setHitlRequest?.(payload.data ?? payload);

      // The run has paused at a human gate — a hard stop for the current turn
      // (if the gate is terminal, no `complete` event ever follows). Finalize
      // the streamed message now and drop the typing/streaming indicators so the
      // thread doesn't look like it's still generating while it waits on the
      // human. Finalize immediately, without the completion grace window, so a
      // fast resume right after the resolve is not clobbered. Streamed text is
      // already committed via contiguous flushes; plan/sources/tool context is
      // left intact for the decision.
      if (lastDeltaTimerRef.current) {
        clearTimeout(lastDeltaTimerRef.current);
        lastDeltaTimerRef.current = null;
      }
      if (bufferRef.current) {
        bufferRef.current.destroy();
        bufferRef.current = null;
      }
      if (currentMessageIdRef.current) {
        usedMessageIdsRef.current.add(currentMessageIdRef.current);
        currentMessageIdRef.current = null;
      }
      completedReceivedRef.current = false;
      refs.current.setAgentIsTyping?.(false);
      refs.current.setIsStreaming?.(false);
      refs.current.setCurrentActivity?.(null);
    },
    []
  );

  const handleLegacyComplete = useCallback(
    (message) => {
      const { conversationUuid, chunkContent, deltaIndex } = normalize(message);

      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;

      const buffer = bufferRef.current;
      if (!buffer) return;

      if (chunkContent) {
        const effectiveIndex = typeof deltaIndex === 'number' ? deltaIndex : buffer.getNextAppendIndex();
        buffer.push(effectiveIndex, chunkContent, message?.sequence);
      }
      buffer.markFin(null);
      completedReceivedRef.current = true;
      graceTimerRef.current = setTimeout(() => {
        graceTimerRef.current = null;
        resetStreamingState();
      }, 3000);
    },
    [resetStreamingState]
  );

  /**
   * A full assistant message delivered as a single committed/end event — NOT a
   * delta stream. This is how the workflow pushes milestone and per-phase
   * progress messages after (or outside) the agent's typing turn. Because the
   * live render used to be coupled to the delta buffer / typing lifecycle, these
   * complete messages had no code path and were dropped until a reload replayed
   * them from history. Upsert them straight into the list (keyed by message id,
   * so a later history fetch dedupes) without touching the streaming indicators.
   */
  const handleCommittedMessage = useCallback(
    (message) => {
      const { conversationUuid, messageId, fullContent, committedItemText, transient, final, responseId } =
        normalize(message);
      // Drop only on an explicit mismatch (see handleInputRequired).
      if (conversationUuid && !isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      // A committed agent reply carries its text under `data.item`: count it as
      // visible text for the run's terminal notice, but never render it here.
      if (typeof committedItemText === 'string' && committedItemText.trim()) noteTextSeen(responseId, messageId);
      const text = typeof fullContent === 'string' ? fullContent : '';
      if (!text.trim()) return;
      // Visible text for this run (milestone steps included) — the terminal
      // `completed` must not read as "no reply" after it.
      noteTextSeen(responseId, messageId);

      // Everything a workflow run says EXCEPT its final message lives inside
      // the run's inline activity accordion (grouped by responseId): transient
      // progress pings as one-line steps, intermediate milestones (brief
      // questions, QA replies, review notices) as full-text steps. Only the
      // final frame (data.final) falls through to a regular bubble. Frames
      // without a responseId are ordinary agent messages — bubbles as always.
      if (responseId && !final) {
        upsertActivityStep(
          responseId,
          messageId,
          text,
          transient ? 'progress' : 'milestone'
        );
        return;
      }

      // If this id is the one actively streaming, let the delta buffer own it
      // (its own completion path finalizes the text) to avoid a double write.
      if (currentMessageIdRef.current && currentMessageIdRef.current === messageId) return;
      usedMessageIdsRef.current.add(messageId);
      updateMessageContent(messageId, text);
    },
    [normalize, updateMessageContent, upsertActivityStep, noteTextSeen]
  );

  const handleConversationMessage = useCallback(
    (message) => {
      const event = String(message?.event ?? message?.type ?? '').toLowerCase();

      // A live event for the ACTIVE conversation means the run is still alive —
      // reset the inactivity safety net. Frames from other conversations must
      // NOT bump it: a parallel run elsewhere would otherwise keep this
      // conversation's "Working…" indicator alive past the window. Frames
      // without a conversation id pass (same relaxed guard the terminal
      // handlers use). Terminal handlers below clear it outright via
      // resetStreamingState, so it only ever fires on true prolonged silence.
      const { conversationUuid } = normalize(message);
      if (!conversationUuid || isSameConversation(activeConversationIdRef.current, conversationUuid)) {
        bumpActivityWatchdog();
      }

      if (event === EVENT_LOWER.OUTPUT_TEXT_DELTA) return handleOutputTextDelta(message);
      if (event === EVENT_LOWER.RESPONSE_CREATED) return handleLifecycleStatus(message, 'created');
      if (event === EVENT_LOWER.RESPONSE_IN_PROGRESS) return handleLifecycleStatus(message, 'in_progress');
      if (event === EVENT_LOWER.RESPONSE_WRITING) return handleLifecycleStatus(message, 'writing');
      if (event === EVENT_LOWER.OUTPUT_ITEM_ADDED) return handleLifecycleStatus(message, 'output_item_added');
      if (event === EVENT_LOWER.RESPONSE_COMPLETED) return handleCompleted(message);
      if (event === EVENT_LOWER.OUTPUT_ITEM_COMMITTED) return handleCommittedMessage(message);
      if (event === EVENT_LOWER.OUTPUT_ITEM_DONE) return handleOutputItemDone(message);
      if (event === EVENT_LOWER.RESPONSE_FINALIZING) return handleFinalizing(message);
      if (event === EVENT_LOWER.RESPONSE_FAILED) return handleFailed(message);
      if (event === EVENT_LOWER.RESPONSE_INCOMPLETE) return handleIncomplete(message);
      if (event === EVENT_LOWER.RESPONSE_CANCELLED || event === EVENT_LOWER.RESPONSE_CANCEL_ACK) return handleCancelled(message);
      if (event === EVENT_LOWER.ACTIVITY_STARTED) return handleActivityStarted(message);
      if (event === EVENT_LOWER.ACTIVITY_DONE) return handleActivityDone(message);
      if (event === EVENT_LOWER.TOOL_CALL_STARTED) return handleToolCallStarted(message);
      if (event === EVENT_LOWER.TOOL_CALL_COMPLETED) return handleToolCallCompleted(message);
      if (event === EVENT_LOWER.TOOL_CALL_FAILED) return handleToolCallFailed(message);
      if (event === EVENT_LOWER.PLAN_PUBLISHED) return handlePlanPublished(message);
      if (event === EVENT_LOWER.PLAN_STEP_UPDATED) return handlePlanStepUpdated(message);
      if (event === EVENT_LOWER.SOURCES_FINAL) return handleSourcesFinal(message);
      if (event === EVENT_LOWER.INPUT_REQUIRED) return handleInputRequired(message);
      if (event === 'delta') return handleLegacyDelta(message);
      if (event === 'complete') return handleLegacyComplete(message);
      // Defensive: a plain full-message push (workflow chat_message / end-message).
      if (event === 'chat_message' || event === 'message') return handleCommittedMessage(message);
    },
    [
      normalize,
      bumpActivityWatchdog,
      handleOutputTextDelta,
      handleLifecycleStatus,
      handleCompleted,
      handleCommittedMessage,
      handleOutputItemDone,
      handleFinalizing,
      handleFailed,
      handleIncomplete,
      handleCancelled,
      handleActivityStarted,
      handleActivityDone,
      handleToolCallStarted,
      handleToolCallCompleted,
      handleToolCallFailed,
      handlePlanPublished,
      handlePlanStepUpdated,
      handleSourcesFinal,
      handleInputRequired,
      handleLegacyDelta,
      handleLegacyComplete,
    ]
  );

  useEffect(() => {
    if (!websocketService?.on) return undefined;
    websocketService.on(CONVERSATION_SCOPE, handleConversationMessage);
    return () => {
      websocketService.off(CONVERSATION_SCOPE, handleConversationMessage);
      if (bufferRef.current) {
        bufferRef.current.destroy();
        bufferRef.current = null;
      }
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
      }
      clearNoticeTimers();
      if (lastDeltaTimerRef.current) {
        clearTimeout(lastDeltaTimerRef.current);
        lastDeltaTimerRef.current = null;
      }
      if (activityWatchdogRef.current) {
        clearTimeout(activityWatchdogRef.current);
        activityWatchdogRef.current = null;
      }
    };
  }, [handleConversationMessage, websocketService, clearNoticeTimers]);

  return {};
};

export { useHomeChatStreaming as useChatStreaming };

